'use client'

import { useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { Id } from '../../../convex/_generated/dataModel'

interface Conversation {
  _id: Id<'assistantConversations'>
  title?: string
  lastMessageAt: number
}

interface ConversationSwitcherProps {
  conversations: Conversation[]
  activeConversationId: Id<'assistantConversations'> | null
  onSelect: (id: Id<'assistantConversations'> | 'new') => void
}

const NEW_CONVERSATION_VALUE = '__new__'

export function ConversationSwitcher({
  conversations,
  activeConversationId,
  onSelect,
}: ConversationSwitcherProps) {
  const t = useTranslations('assistant.conversationSwitcher')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const activeConversation = conversations.find((c) => c._id === activeConversationId)
  const label = activeConversation?.title ?? t('newConversation')

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleSelect = (value: string) => {
    setOpen(false)
    if (value === NEW_CONVERSATION_VALUE) {
      onSelect('new')
    } else {
      onSelect(value as Id<'assistantConversations'>)
    }
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
      {/* Trigger pill */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.3rem',
          padding: '0.25rem 0.6rem',
          borderRadius: '9999px',
          border: '1px solid var(--color-border)',
          background: 'var(--color-surface-hover)',
          fontSize: '0.75rem',
          color: 'var(--color-text-secondary)',
          cursor: 'pointer',
          maxWidth: '100%',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        <span
          // Titles are derived from user messages and may be RTL (e.g. Hebrew).
          dir="auto"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '120px',
          }}
        >
          {label}
        </span>
        <ChevronDown
          style={{
            width: '10px',
            height: '10px',
            flexShrink: 0,
            transition: 'transform 150ms',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 200,
            minWidth: '180px',
            maxWidth: '240px',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '0.75rem',
            boxShadow: 'var(--shadow-lg)',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '0.25rem' }}>
            <DropdownItem
              value={NEW_CONVERSATION_VALUE}
              label={t('newConversationItem')}
              active={activeConversationId == null}
              onSelect={handleSelect}
            />
            {conversations.map((c) => (
              <DropdownItem
                key={c._id}
                value={c._id}
                label={c.title ?? t('untitled')}
                active={c._id === activeConversationId}
                onSelect={handleSelect}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function DropdownItem({
  value,
  label,
  active,
  onSelect,
}: {
  value: string
  label: string
  active: boolean
  onSelect: (value: string) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      // Conversation titles may be RTL; auto direction keeps them readable
      // and aligns them to their natural side.
      dir="auto"
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'start',
        padding: '0.4rem 0.6rem',
        borderRadius: '0.5rem',
        fontSize: '0.75rem',
        fontWeight: active ? 600 : 400,
        color: active ? 'var(--color-lime)' : 'var(--color-text-secondary)',
        background: active ? 'var(--color-lime-muted)' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        transition: 'background 100ms',
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'var(--color-surface-hover)'
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'transparent'
        }
      }}
    >
      {label}
    </button>
  )
}
